'use client';
import React from 'react';
import { adminStats, levels, adminUsers } from '@/lib/mockData';
import type { Language } from '@/lib/i18n';
import { Users, BookOpen, Flame, TrendingUp, TrendingDown } from 'lucide-react';

const ICON_MAP: Record<string, React.ReactNode> = {
  Users: <Users size={20} />,
  BookOpen: <BookOpen size={20} />,
  Flame: <Flame size={20} />,
  TrendingUp: <TrendingUp size={20} />,
};

interface Props { language: Language }

export default function AdminOverview({ language }: Props) {
  const totalLessons = levels.reduce((acc, l) => acc + l.lessons.length, 0);
  const activeUsers = adminUsers.filter((u) => u.status === 'active').length;

  return (
    <div className="space-y-6">
      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {adminStats.map((stat) => (
          <div key={`admin-stat-${stat.label.fr}`} className="bg-card rounded-2xl border border-border card-shadow p-5">
            <div className="flex items-start justify-between mb-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                style={{ backgroundColor: stat.color }}
              >
                {ICON_MAP[stat.icon]}
              </div>
              <div
                className={`flex items-center gap-1 text-xs font-600 ${
                  stat.change >= 0 ? 'text-success' : 'text-danger'
                }`}
              >
                {stat.change >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                {Math.abs(stat.change)}%
              </div>
            </div>
            <p className="text-2xl font-800 text-foreground font-tabular">{stat.value}</p>
            <p className="text-xs font-500 text-muted-foreground mt-1 uppercase tracking-wide">
              {stat.label[language]}
            </p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card rounded-2xl border border-border card-shadow p-5">
          <h3 className="text-base font-600 text-foreground mb-4">
            {language === 'fr' ? 'Complétion par niveau' : 'Завершение по уровням'}
          </h3>
          {/* Inline chart to avoid circular import */}
          <div className="space-y-2">
            {levels.map((level) => (
              <div key={`chart-level-${level.id}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">
                    {language === 'fr' ? `Niveau ${level.number}` : `Уровень ${level.number}`}
                  </span>
                  <span className="font-700 text-foreground">{level.completionPercent}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${level.completionPercent}%`, backgroundColor: level.color }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border card-shadow p-5">
          <h3 className="text-base font-600 text-foreground mb-4">
            {language === 'fr' ? 'Résumé de la plateforme' : 'Сводка платформы'}
          </h3>
          <div className="space-y-4">
            {[
              { label: language === 'fr' ? 'Niveaux actifs' : 'Активных уровней', value: 10, max: 10, color: '#7C3AED' },
              { label: language === 'fr' ? 'Leçons totales' : 'Всего уроков', value: totalLessons, max: totalLessons, color: '#10B981' },
              { label: language === 'fr' ? 'Utilisateurs actifs' : 'Активных пользователей', value: activeUsers, max: adminUsers.length, color: '#3B82F6' },
            ].map((item) => (
              <div key={`overview-item-${item.label}`}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-muted-foreground font-500">{item.label}</span>
                  <span className="font-700 text-foreground">{item.value}/{item.max}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.round((item.value / item.max) * 100)}%`,
                      backgroundColor: item.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}